import React, { ChangeEvent, useMemo } from "react";

import { Input } from "@/components/common";
import { Filter } from "@/components/dashboard/parcels/parcels-filter/filter";
import DateRangeFilter from "@/components/dashboard/parcels/parcels-filter/date-range-filter";
import {
  DeliveryServiceIcon,
  FilterCategoryIcon,
  FilterFlagIcon,
  FilterTransportIcon,
  PlaneIcon,
  SearchIcon,
  ShipIcon,
} from "@/components/assets/icons";
import { useGetAllFromCountriesRequest } from "@/api/useGetAllFromCountriesRequest";
import { useGetShipmentTypesRequest } from "@/api/useGetShipmentTypesRequest";
import { renderCountryIcon } from "@/utils/iconRenderer";

import { FilterType, Theme } from "@/enums";
import { useTheme } from "@/context/Theme.context";
import {
  IDateRangeFilter,
  IFilterOption,
  IParcelFilter,
} from "@/types/parcel-filter.types";
import { IFilterItem } from "./arrived-parcels-filter-box";
import { useTranslations } from "next-intl";

type TProps = {
  filters: IParcelFilter;
  onFilterChange: (filters: IParcelFilter) => void;
  searchValue: string;
  onSearchChange: (search: string) => void;
  showSendDate?: boolean;
};

const ParcelsFilterBox = ({
  filters,
  onFilterChange,
  searchValue,
  onSearchChange,
  showSendDate = false,
}: TProps) => {
  const t = useTranslations();
  const theme = useTheme();

  const { data: countriesData } = useGetAllFromCountriesRequest();
  const { data: productCategoriesData } = useGetShipmentTypesRequest();

  const countries = countriesData?.data?.data || [];
  const productCategories = productCategoriesData?.data?.data || [];

  const countriesOptions = useMemo(() => {
    if (!countries) return [];
    return countries.map((item) => ({
      label: item.countryName,
      value: item.countryId,
      icon: renderCountryIcon[item.countryId],
    }));
  }, [countries]);

  const categoryOptions = useMemo(() => {
    if (!productCategories) return [];
    return productCategories.map((item) => ({
      label: item.shipmentTypeName,
      value: item.shipmentTypeId,
    }));
  }, [productCategories]);

  const transportOptions: IFilterOption[] = [
    { label: "air", value: 1, icon: PlaneIcon },
    { label: "sea", value: 2, icon: ShipIcon },
  ];

  if (theme === Theme.UK) {
    transportOptions.push({ label: "economy", value: 4, icon: DeliveryServiceIcon });
  }

  const filterItemsList = useMemo<IFilterItem[]>(() => {
    return [
      {
        label: "country",
        icon: FilterFlagIcon,
        value: filters.country,
        type: FilterType.COUNTRY,
        options: countriesOptions,
        show: true,
        translation: true,
      },
      {
        label: "category",
        icon: FilterCategoryIcon,
        value: filters.category,
        type: FilterType.CATEGORY,
        options: categoryOptions,
        show: true,
        translation: false,
      },
      {
        label: "transport",
        icon: FilterTransportIcon,
        value: filters.transport,
        type: FilterType.TRANSPORT,
        options: transportOptions,
        show: theme !== Theme.UZ,
        translation: true,
      },
    ];
  }, [filters, categoryOptions, countriesOptions, theme]);

  const handleFilterChange = (type: FilterType, value: any) => {
    onFilterChange({
      ...filters,
      [type]: (filters[type] as any[]).includes(value)
        ? (filters[type] as any[]).filter((item) => item !== value)
        : [...(filters[type] as any[]), value],
    });
  };

  const handleDateRangeChange = (type: FilterType, value: IDateRangeFilter) => {
    onFilterChange({ ...filters, [type]: value });
  };

  const handleSelectAll = (type: FilterType, options: { value: any }[]) => {
    onFilterChange({
      ...filters,
      [type]:
        (filters[type] as any[]).length === options.length
          ? []
          : options.map((item) => item.value),
    });
  };

  return (
    <div className="mt-4 grid grid-cols-2 items-center gap-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      <Input
        className="!h-14"
        label="search"
        icon={SearchIcon}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onSearchChange(e.target.value);
        }}
        value={searchValue}
      />
      {filterItemsList
        .filter((e) => e.show)
        .map((item, index) => (
          <Filter
            key={item.label}
            item={item}
            handleFilterChange={handleFilterChange}
            handleSelectAll={handleSelectAll}
            withBorder={index < filterItemsList.filter((e) => e.show).length - 1}
          />
        ))}
      <DateRangeFilter
        label="registration_date"
        value={filters.registrationDate}
        onChange={(val) =>
          handleDateRangeChange(FilterType.REGISTRATION_DATE, val)
        }
        withBorder={showSendDate}
      />
      {showSendDate && (
        <DateRangeFilter
          label="send_date"
          value={filters.sendDate}
          onChange={(val) => handleDateRangeChange(FilterType.SEND_DATE, val)}
          withBorder={false}
        />
      )}
    </div>
  );
};

export default ParcelsFilterBox;
